import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Alert,
  Chip,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, ViewList as ViewListIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import clientService from '../services/clientService';
import { ClientDTO } from '../types/client.types';
import { formatToUIDateTime } from '../utils/dateUtils';

const ClientManagementPage: React.FC = () => {
  const [clients, setClients] = useState<ClientDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentClient, setCurrentClient] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    contactEmail: '',
    contactPhone: '',
    status: 'Active',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await clientService.getAllClients(0, 100);
      setClients(response.content || []);
      setErrorMessage('');
    } catch (error: any) {
      console.error('Error fetching clients:', error);
      setErrorMessage(error.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Client name is required';
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddClient = () => {
    setDialogMode('add');
    setFormData({
      name: '',
      contactEmail: '',
      contactPhone: '',
      status: 'Active',
    });
    setCurrentClient(null);
    setErrors({});
    setSuccessMessage('');
    setOpenDialog(true);
  };

  const handleEditClient = (client: any) => {
    setDialogMode('edit');
    setFormData({
      name: client.name,
      contactEmail: client.contactEmail,
      contactPhone: client.contactPhone,
      status: client.status,
    });
    setCurrentClient(client);
    setErrors({});
    setSuccessMessage('');
    setOpenDialog(true);
  };

  const handleDeleteClient = async (id: string) => {
    if (window.confirm(`Are you sure you want to delete this client? This will also delete all associated facilities. `)) {
      try {
        await clientService.deleteClient(id);
        setClients(clients.filter(c => c.id !== id));
        setSuccessMessage('Client deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error: any) {
        setErrorMessage(error.message || 'Failed to delete client');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (dialogMode === 'add') {
        const newClient = await clientService.createClient({
          name: formData.name,
          code: formData.name.toLowerCase().replace(/\s+/g, '-'), // Generate code from name
          email: formData.contactEmail,
          phone: formData.contactPhone,
          country: 'USA', // Default country
        });

        setClients([...clients, newClient]);
        setSuccessMessage('Client added successfully');
      } else if (currentClient && currentClient.id) {
        const updatedClient = await clientService.updateClient(currentClient.id, {
          name: formData.name,
          email: formData.contactEmail,
          phone: formData.contactPhone,
          isActive: formData.status === 'Active',
        });

        const updatedClients = clients.map(c =>
          c.id === currentClient.id ? updatedClient : c
        );

        setClients(updatedClients);
        setSuccessMessage('Client updated successfully');
      }

      setOpenDialog(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to save client');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Client Management
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
        <Typography variant="h6">Manage Healthcare Clients</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClient}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          Add New Client
        </Button>
      </Paper>

      {!loading && (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>Contact Phone</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No clients found
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>{client.name}</TableCell>
                    <TableCell>{client.email || 'N/A'}</TableCell>
                    <TableCell>{client.phone || 'N/A'}</TableCell>
                    <TableCell>{client.code || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={client.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={client.isActive ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>{client.createdAt ? formatToUIDateTime(client.createdAt) : 'N/A'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClient(client)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        component={Link}
                        to={`/super-admin/clients/${client.id}/facilities`}
                        sx={{ mr: 1 }}
                      >
                        <ViewListIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => client.id && handleDeleteClient(client.id)}
                        disabled={!client.id}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Client Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Client' : 'Edit Client'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialogMode === 'add'
              ? 'Enter the details for the new healthcare client.'
              : 'Update the details for this healthcare client.'}
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Client Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="contactEmail"
            label="Contact Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.contactEmail}
            onChange={handleChange}
            error={!!errors.contactEmail}
            helperText={errors.contactEmail}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="contactPhone"
            label="Contact Phone"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.contactPhone}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="status"
            label="Status"
            select
            fullWidth
            variant="outlined"
            value={formData.status}
            onChange={handleChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'add' ? 'Add Client' : 'Update Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientManagementPage;